import { useEffect, useMemo, useState } from "react";
import { Building2, Mail, MapPin, Plus, Search, User } from "lucide-react";
import {
  createCustomer,
  getCustomers,
  updateCustomer,
} from "../services/customerService";

function getTypeLabel(type) {
  if (!type) return "Unknown";

  const labels = {
    private: "Private",
    business: "Business",
    erhverv: "Business",
    privat: "Private",
  };

  return labels[String(type).toLowerCase()] || type;
}

function getInitials(name = "") {
  return (
    name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "?"
  );
}

export default function Customers() {

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [editingCustomer, setEditingCustomer] = useState(null);

  useEffect(() => {
  fetchCustomers();
}, []);

  async function fetchCustomers() {
  setLoading(true);

  try {
    const data = await getCustomers();
    setCustomers(Array.isArray(data) ? data : []);
  } catch (error) {
    console.error("Failed to fetch customers:", error);
    setCustomers([]);
  } finally {
    setLoading(false);
  }
}

  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const query = search.toLowerCase().trim();

      const matchesSearch =
        !query ||
        [customer.name, customer.email, customer.city, customer.phone]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query));

      const normalizedType = String(customer.type || "").toLowerCase();

      const matchesType =
        typeFilter === "all" ||
        normalizedType === typeFilter ||
        (typeFilter === "private" && normalizedType === "privat") ||
        (typeFilter === "business" && normalizedType === "erhverv");

      return matchesSearch && matchesType;
    });
  }, [customers, search, typeFilter]);

  const privateCount = customers.filter((customer) =>
    ["private", "privat"].includes(String(customer.type || "").toLowerCase())
  ).length;

  const businessCount = customers.filter((customer) =>
    ["business", "erhverv"].includes(String(customer.type || "").toLowerCase())
  ).length;

  return (
    <div className="min-h-full bg-[#f3f2f1] p-6 text-[#201f1e]">
      <div className="mx-auto max-w-7xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Customers</h1>

          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 rounded-sm bg-[#0f6cbd] px-3 py-2 text-sm font-medium text-white hover:bg-[#115ea3]"
          >
            <Plus size={16} />
            New customer
          </button>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <Metric label="Customers" value={customers.length} />
          <Metric label="Private" value={privateCount} />
          <Metric label="Business" value={businessCount} />
        </div>

        <section className="overflow-hidden rounded-sm border border-[#edebe9] bg-white">
          <div className="flex flex-wrap items-center gap-3 border-b border-[#edebe9] bg-[#faf9f8] px-4 py-3">
            <div className="relative min-w-[260px] flex-1">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#605e5c]"
              />

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search customers"
                className="h-9 w-full rounded-sm border border-[#d2d0ce] bg-white pl-9 pr-3 text-sm text-[#201f1e] outline-none focus:border-[#0f6cbd]"
              />
            </div>

            <select
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
              className="h-9 rounded-sm border border-[#d2d0ce] bg-white px-3 text-sm text-[#201f1e] outline-none focus:border-[#0f6cbd]"
            >
              <option value="all">All</option>
              <option value="private">Private</option>
              <option value="business">Business</option>
            </select>
          </div>

          {loading ? (
            <EmptyState title="Loading" />
          ) : filteredCustomers.length === 0 ? (
            <EmptyState title="No customers" />
          ) : (
            <div className="divide-y divide-[#edebe9]">
              {filteredCustomers.map((customer) => (
                <CustomerRow
                  key={customer.id}
                  customer={customer}
                  onClick={() => setSelectedCustomer(customer)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {showCreateModal && (
        <CustomerModal
  onClose={() => setShowCreateModal(false)}
  onSaved={() => {
    setShowCreateModal(false);
    fetchCustomers();
  }}
/>
      )}

      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onEdit={() => setEditingCustomer(selectedCustomer)}
        />
      )}

      {editingCustomer && (
        <CustomerModal
  customer={editingCustomer}
  onClose={() => setEditingCustomer(null)}
  onSaved={() => {
    setEditingCustomer(null);
    setSelectedCustomer(null);
    fetchCustomers();
  }}
/>
      )}
    </div>
  );
}

function CustomerRow({ customer, onClick }) {
  const isBusiness = ["business", "erhverv"].includes(
    String(customer.type || "").toLowerCase()
  );

  return (
    <button
      type="button"
      onClick={onClick}
      className="grid w-full grid-cols-[minmax(220px,1.2fr)_minmax(140px,0.8fr)_minmax(180px,1fr)_minmax(120px,0.7fr)] items-center gap-4 px-4 py-3 text-left hover:bg-[#faf9f8]"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-[#f3f2f1] text-xs font-semibold text-[#323130]">
          {getInitials(customer.name)}
        </div>

        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[#201f1e]">
            {customer.name || "Unnamed customer"}
          </div>

          {customer.phone && (
            <div className="mt-0.5 text-xs text-[#605e5c]">
              {customer.phone}
            </div>
          )}
        </div>
      </div>

      <div>
        <span className="inline-flex items-center gap-1 rounded-sm bg-[#f3f2f1] px-2 py-1 text-xs font-medium text-[#605e5c]">
          {isBusiness ? <Building2 size={13} /> : <User size={13} />}
          {getTypeLabel(customer.type)}
        </span>
      </div>

      <div className="min-w-0">
        {customer.email && (
          <div className="flex min-w-0 items-center gap-2 text-sm text-[#323130]">
            <Mail size={14} className="shrink-0 text-[#605e5c]" />
            <span className="truncate">{customer.email}</span>
          </div>
        )}
      </div>

      <div className="min-w-0">
        {customer.city && (
          <div className="flex items-center gap-2 text-sm text-[#605e5c]">
            <MapPin size={14} />
            <span className="truncate">{customer.city}</span>
          </div>
        )}
      </div>
    </button>
  );
}

function CustomerDetailsModal({ customer, onClose, onEdit }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-sm bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-[#201f1e]">
              {customer.name || "Customer"}
            </h2>
            <div className="mt-0.5 text-xs text-[#605e5c]">
              {getTypeLabel(customer.type)}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-sm px-2 py-1 text-sm text-[#605e5c] hover:bg-[#f3f2f1]"
          >
            Close
          </button>
        </div>

        <div className="grid gap-4 p-4 md:grid-cols-2">
          <Info label="Email" value={customer.email} />
          <Info label="Phone" value={customer.phone} />
          <Info label="City" value={customer.city} />
          <Info label="Type" value={getTypeLabel(customer.type)} />
        </div>

        <div className="border-t border-[#edebe9] p-4">
          <div className="grid gap-2 md:grid-cols-3">
            <ModalButton>Create quote</ModalButton>
            <ModalButton onClick={onEdit}>Edit customer</ModalButton>
            <ModalButton>Archive</ModalButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomerModal({ customer, onClose, onSaved }) {
  const isEditing = !!customer;

  const [form, setForm] = useState({
    name: customer?.name || "",
    type: customer?.type || "private",
    city: customer?.city || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function handleSubmit(event) {
  event.preventDefault();

  if (!form.name.trim()) {
    setError("Name required");
    return;
  }

  setSaving(true);
  setError("");

  try {
    const payload = {
      name: form.name.trim(),
      type: form.type,
      city: form.city.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
    };

    if (isEditing) {
      await updateCustomer(customer.id, payload);
    } else {
      await createCustomer(payload);
    }

    onSaved?.();
  } catch (error) {
    setError(error.message || "Could not save customer");
  } finally {
    setSaving(false);
  }
}

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-sm bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-[#edebe9] bg-[#faf9f8] px-4 py-3">
          <h2 className="text-sm font-semibold text-[#201f1e]">
            {isEditing ? "Edit customer" : "New customer"}
          </h2>

          <button
            type="button"
            onClick={onClose}
            className="rounded-sm px-2 py-1 text-sm text-[#605e5c] hover:bg-[#f3f2f1]"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {error && (
            <div className="rounded-sm bg-[#fff4ce] px-3 py-2 text-sm text-[#8a6d1d]">
              {error}
            </div>
          )}

          <Field label="Name">
            <input
              value={form.name}
              onChange={(event) => updateField("name", event.target.value)}
              autoFocus
              className="h-9 w-full rounded-sm border border-[#d2d0ce] px-3 text-sm outline-none focus:border-[#0f6cbd]"
            />
          </Field>

          <Field label="Type">
            <select
              value={form.type}
              onChange={(event) => updateField("type", event.target.value)}
              className="h-9 w-full rounded-sm border border-[#d2d0ce] px-3 text-sm outline-none focus:border-[#0f6cbd]"
            >
              <option value="private">Private</option>
              <option value="business">Business</option>
            </select>
          </Field>

          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Email">
              <input
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
                type="email"
                className="h-9 w-full rounded-sm border border-[#d2d0ce] px-3 text-sm outline-none focus:border-[#0f6cbd]"
              />
            </Field>

            <Field label="Phone">
              <input
                value={form.phone}
                onChange={(event) => updateField("phone", event.target.value)}
                className="h-9 w-full rounded-sm border border-[#d2d0ce] px-3 text-sm outline-none focus:border-[#0f6cbd]"
              />
            </Field>
          </div>

          <Field label="City">
            <input
              value={form.city}
              onChange={(event) => updateField("city", event.target.value)}
              className="h-9 w-full rounded-sm border border-[#d2d0ce] px-3 text-sm outline-none focus:border-[#0f6cbd]"
            />
          </Field>

          <div className="flex justify-end gap-2 border-t border-[#edebe9] pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm border border-[#edebe9] bg-white px-3 py-2 text-sm text-[#323130] hover:bg-[#f3f2f1]"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-sm bg-[#0f6cbd] px-3 py-2 text-sm font-medium text-white hover:bg-[#115ea3] disabled:opacity-50"
            >
              {saving ? "Saving" : isEditing ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-sm border border-[#edebe9] bg-[#faf9f8] px-3 py-2">
      <div className="text-xs font-medium text-[#605e5c]">{label}</div>
      <div className="mt-1 text-sm font-medium text-[#201f1e]">
        {value || "—"}
      </div>
    </div>
  );
}

function ModalButton({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-sm border border-[#edebe9] bg-white px-3 py-2 text-left text-sm font-medium text-[#323130] hover:bg-[#f3f2f1]"
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-[#605e5c]">{label}</div>
      {children}
    </label>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-sm border border-[#edebe9] bg-white p-4">
      <div className="text-2xl font-semibold tabular-nums text-[#201f1e]">
        {value}
      </div>

      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-[#605e5c]">
        {label}
      </div>
    </div>
  );
}

function EmptyState({ title }) {
  return <div className="px-4 py-10 text-sm text-[#605e5c]">{title}</div>;
}